import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { selectProductInShops } from '@selectors/shop';

import { distance } from '@utils/math';
import {
  debounceAsync,
  getLatAndLngFromQuery,
  MAX_STORES_DISTANCE,
  selectNearbyShops,
  selectShopsByQuery,
  sortStoresByDistance,
} from '@utils/shop';

import {
  GEOLOCATION_PERMISSION_STATUS,
  SEARCH_MODE,
} from '@common/constants/storeFinder';
import LoadingIndicator from '@common/components/LoadingIndicator';

import SearchInput from './SearchInput';
import SearchResults from './SearchResults';
import SearchMessage from './StoreMessage';

function StoreSearch({ productColorId }) {
  const [isSearching, setIsSearching] = useState(false);
  const [searchMode, setSearchMode] = useState(SEARCH_MODE.TEXT);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const [geolocationPermissionStatus, setGeolocationPermissionStatus] =
    useState(GEOLOCATION_PERMISSION_STATUS.PROMPT);
  const [isWaitingForPosition, setIsWaitingForPosition] = useState(false);

  useEffect(() => {
    navigator.permissions?.query({ name: 'geolocation' }).then(({ state }) => {
      if (state !== geolocationPermissionStatus) {
        setGeolocationPermissionStatus(state);
      }
    });
  }, []);

  const isGeolocationMode = searchMode === SEARCH_MODE.GEOLOCATION;

  const storesList = useSelector(state => state.shop.entities.stores);
  const shopIds = searchResults.map(({ erpStoreId }) => erpStoreId);
  const productsInShops = useSelector(
    selectProductInShops(shopIds, productColorId),
  );
  const sizeSymbols = productsInShops.map(product =>
    product
      .filter(({ availability }) => availability > 0)
      .map(({ title }) => title),
  );

  const shouldShowResults =
    (isGeolocationMode &&
      !isWaitingForPosition &&
      geolocationPermissionStatus === GEOLOCATION_PERMISSION_STATUS.GRANTED) ||
    (!isSearching && !isGeolocationMode && !!query);
  const hasShopResults = !!searchResults.length;

  const handleStoresByRadius = useCallback(
    debounceAsync(async (regularResults, q) => {
      try {
        const location = (await getLatAndLngFromQuery({ address: q }))
          ?.results?.[0];

        if (!location) {
          return regularResults;
        }

        const {
          geometry: {
            location: { lat, lng },
          },
        } = location;

        const storesInRadius = Object.values(storesList).reduce(
          (filteredStores, store) => {
            const storeDistance = distance(lat, lng, store.lat, store.lng);

            const shouldReturnStore = storeDistance <= MAX_STORES_DISTANCE;

            return shouldReturnStore
              ? [
                  ...filteredStores,
                  {
                    ...store,
                    distance: storeDistance,
                  },
                ]
              : filteredStores;
          },
          [],
        );

        const removedDuplicates = storesInRadius
          .filter(
            store =>
              !regularResults.some(
                ({ erpStoreId }) => erpStoreId === store.erpStoreId,
              ),
          )
          .sort(sortStoresByDistance);

        return [regularResults, removedDuplicates].flat();
      } catch (error) {
        return regularResults;
      }
    }, 250),
    [storesList],
  );

  const getLatLongSuccess = ({ coords: { latitude, longitude } }) => {
    setIsWaitingForPosition(false);
    setGeolocationPermissionStatus(GEOLOCATION_PERMISSION_STATUS.GRANTED);
    setQuery('');
    setSearchResults(selectNearbyShops(latitude, longitude, 15, storesList));
  };

  const getLatLongError = ({ code }) => {
    setIsWaitingForPosition(false);
    setGeolocationPermissionStatus(
      code === 1
        ? GEOLOCATION_PERMISSION_STATUS.DENIED
        : GEOLOCATION_PERMISSION_STATUS.ERROR,
    );
  };

  const searchByGeolocation = () => {
    if (geolocationPermissionStatus === GEOLOCATION_PERMISSION_STATUS.ERROR) {
      setGeolocationPermissionStatus(GEOLOCATION_PERMISSION_STATUS.PROMPT);
    }

    setIsWaitingForPosition(true);
    setSearchMode(SEARCH_MODE.GEOLOCATION);
    navigator.geolocation.getCurrentPosition(
      getLatLongSuccess,
      getLatLongError,
    );
    setQuery('');
  };

  const searchByText = useCallback(
    async ({ target: { value } }) => {
      setIsSearching(true);
      setSearchMode(SEARCH_MODE.TEXT);
      setQuery(value);

      const regularResults = selectShopsByQuery(value, storesList);

      if (!value) {
        setSearchResults([]);
        setIsSearching(false);

        return;
      }

      const result = await handleStoresByRadius(regularResults, value);

      setSearchResults(result);
      setIsSearching(false);
    },
    [handleStoresByRadius, storesList],
  );

  // useEffect(() => {
  // Triggering geolocation permission request, so that the user can accept or deny it
  //   navigator.geolocation.getCurrentPosition(() => {
  //     setIsWaitingForPosition(false);
  //   }, getLatLongError);
  // }, []);

  return (
    <>
      <SearchInput
        query={query}
        onQueryChange={searchByText}
        onNearMeClick={searchByGeolocation}
        isLocationSearchDisabled={
          isWaitingForPosition ||
          (isGeolocationMode &&
            geolocationPermissionStatus ===
              GEOLOCATION_PERMISSION_STATUS.DENIED)
        }
      />

      {shouldShowResults && hasShopResults && (
        <SearchResults
          searchResults={searchResults}
          sizeSymbols={productColorId ? sizeSymbols : undefined}
          dataTestId="other-stores-grid"
        />
      )}

      <SearchMessage
        isGeolocationMode={isGeolocationMode}
        geolocationPermissionStatus={geolocationPermissionStatus}
        shouldShowResults={shouldShowResults}
        hasShopResults={hasShopResults}
      />

      {((isWaitingForPosition &&
        geolocationPermissionStatus ===
          GEOLOCATION_PERMISSION_STATUS.GRANTED) ||
        isSearching) && (
        <LoadingIndicator
          isLarge
          withWrapper
          spacingTop={['section', 2]}
        />
      )}
    </>
  );
}

StoreSearch.propTypes = {
  productColorId: PropTypes.string,
};

StoreSearch.defaultProps = {
  productColorId: undefined,
};

export default StoreSearch;
